import { useEffect, useRef, useState } from 'react';

// Feature progress is a single shared value (FeatureStore.progress) tied to the
// current feature. When autoplay advances to the next feature, that shared value
// can momentarily still read the PREVIOUS feature's value (e.g. 100) before the
// new run's own progress starts — which flashed as "100 -> 0" on the next
// feature's loader.
//
// This hook stages the value per card so a loader always starts from 0:
//  - while selected it shows 0 until it has seen a genuinely fresh low reading
//    (progress near 0) for this run, so a carried-over high value is never shown;
//  - once following, it ignores a large upward SPIKE — a stale carried-over
//    reading that appears right after the fresh start — since real playback only
//    ever rises gradually;
//  - the width transition is forward-only, so a reset never animates backward.
const SPIKE = 40; // %: a single-update jump larger than this is a stale value.

export function useStagedProgress(selected: boolean, progress: number | null | undefined) {
  const cur = selected && progress != null ? progress : 0;
  const [shown, setShown] = useState(0);
  // `armed` becomes true once this card's own run has demonstrably started.
  const armed = useRef(false);

  useEffect(() => {
    if (!selected) {
      armed.current = false;
      setShown(0);
      return;
    }
    if (!armed.current) {
      // Hold at 0 until this run's own progress begins (a fresh low reading);
      // any carried-over high value from the previous feature is ignored.
      if (cur <= 2) {
        armed.current = true;
        setShown(cur);
      }
      return;
    }
    // Armed: follow live progress, but reject a stale upward spike (a value that
    // jumps far ahead in one update — real playback can't do that).
    setShown((prev) => (cur - prev > SPIKE ? prev : cur));
  }, [selected, cur]);

  // Animate forward only; decreases (reset / feature switch) snap instantly so a
  // reset is never seen as a backward 100 -> 0 sweep.
  const prevShownRef = useRef(0);
  const transition = shown < prevShownRef.current - 0.01 ? 'none' : 'width 0.18s linear';
  useEffect(() => {
    prevShownRef.current = shown;
  }, [shown]);

  return { shown, transition };
}
